import { Routes } from '@angular/router';
import { provideSavingPlansDomain } from '@zero/saving-plans/domain';
import { LoggedInGuard, PotentialCustomerGuard } from '@zero/shared/util-web-app-services';
import { OnboardingStepGuard } from './guards/onboarding-step.guard';
import { SavingsPlanPreviewComponent } from './pages/savings-plan-preview/savings-plan-preview.component';

export const ONBOARDING_WEB_ROUTES: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/sign-up/sign-up.component').then((m) => m.SignUpComponent),
    data: { shouldHideHeaderAndFooter: true },
  },
  {
    path: 'preview',
    canActivate: [PotentialCustomerGuard],
    children: [
      {
        path: 'sparplaene',
        component: SavingsPlanPreviewComponent,
        providers: [provideSavingPlansDomain()],
      },
    ],
  },
  {
    path: 'registrieren',
    canActivate: [OnboardingStepGuard],
    loadComponent: () => import('./pages/sign-up/sign-up.component').then((m) => m.SignUpComponent),
  },
  {
    path: 'passwort-eingeben',
    canActivate: [OnboardingStepGuard],
    loadComponent: () => import('./pages/password/password.component').then((m) => m.PasswordComponent),
  },
  {
    path: 'email-bestaetigen',
    canActivate: [OnboardingStepGuard],
    loadComponent: () => import('./pages/email-confirmation/email-confirmation.component').then((m) => m.EmailConfirmationComponent),
  },
  {
    path: 'registrieren/erneutbestaetigen',
    loadComponent: () => import('./pages/sign-up-resend/sign-up-resend.component').then((m) => m.SignUpResendComponent),
  },
  {
    path: 'einloggen',
    canActivate: [OnboardingStepGuard],
    loadComponent: () =>
      import('./pages/login-after-email-confirmation/login-after-email-confirmation.component').then(
        (m) => m.LoginAfterEmailConfirmationComponent,
      ),
  },
  {
    path: 'weiterleitung',
    canActivate: [LoggedInGuard],
    loadComponent: () => import('./pages/redirect/redirect.component').then((m) => m.RedirectComponent),
  },
  {
    path: 'mobilfunknummer',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/mobile-number/mobile-number.component').then((m) => m.MobileNumberComponent),
    data: { animation: 'MobileNumberPage' },
  },
  {
    path: 'person',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/person/person.component').then((m) => m.PersonComponent),
    data: { animation: 'PersonPage' },
  },
  {
    path: 'wohnsitz',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/residence/residence.component').then((m) => m.ResidenceComponent),
    data: { animation: 'ResidencePage' },
  },
  {
    path: 'staatsangehoerigkeit',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/nationality/nationality.component').then((m) => m.NationalityComponent),
    data: { animation: 'NationalityPage' },
  },
  {
    path: 'referenzkonto',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/bank-account/bank-account.component').then((m) => m.BankAccountComponent),
    data: { animation: 'BankAccountPage' },
  },
  {
    path: 'ersteinzahlung',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/initial-deposit/initial-deposit.component').then((m) => m.InitialDepositComponent),
    data: { animation: 'InitialDepositPage' },
  },
  {
    path: 'gebuehrenfreier-handel',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/provision/provision.component').then((m) => m.ProvisionComponent),
    data: { animation: 'ProvisionPage' },
  },
  {
    path: 'identifikation',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/ident/ident.component').then((m) => m.IdentComponent),
    data: { animation: 'IdentPage' },
  },
  {
    path: 'in-bearbeitung',
    canActivate: [OnboardingStepGuard, PotentialCustomerGuard],
    loadComponent: () => import('./pages/in-progress/in-progress.component').then((m) => m.InProgressComponent),
    data: { animation: 'InProgressPage' },
  },
  {
    path: 'sparplan-anlegen/vorschlag',
    canActivate: [PotentialCustomerGuard],
    loadComponent: () =>
      import('./pages/savings-plan/favorite-suggestion/favorite-suggestion.component').then((m) => m.FavoriteSuggestionComponent),
    data: { animation: 'FavoriteSuggestionsPage' },
  },
  {
    path: 'sparplan-anlegen/top-3-anbieter',
    canActivate: [PotentialCustomerGuard],
    loadComponent: () => import('./pages/savings-plan/top-3-issuer/top-3-issuer.component').then((m) => m.Top3IssuerComponent),
    data: { animation: 'Top3IssuerPage' },
  },
  {
    path: 'sparplan-anlegen/alternative',
    canActivate: [PotentialCustomerGuard],
    loadComponent: () => import('./pages/savings-plan/alternative/alternative.component').then((m) => m.AlternativeComponent),
    data: { animation: 'SavingsPlanAlternativePage' },
    providers: [provideSavingPlansDomain()],
  },
  {
    path: 'sparplan-anlegen/erstellen/:isin',
    canActivate: [PotentialCustomerGuard],
    loadComponent: () => import('./pages/savings-plan/create/create.component').then((m) => m.CreateComponent),
    data: { animation: 'SavingsPlanCreatePage' },
    providers: [provideSavingPlansDomain()],
  },
  {
    path: 'sparplan-anlegen/pruefen',
    canActivate: [PotentialCustomerGuard],
    loadComponent: () => import('./pages/savings-plan/confirm/confirm.component').then((m) => m.ConfirmComponent),
    data: { animation: 'SavingsPlanConfirmPage' },
    providers: [provideSavingPlansDomain()],
  },
  {
    path: 'sparplan-anlegen/erfolgreich',
    canActivate: [PotentialCustomerGuard],
    loadComponent: () => import('./pages/savings-plan/success/success.component').then((m) => m.SuccessComponent),
    data: { animation: 'SavingsPlanSuccessPage' },
    providers: [provideSavingPlansDomain()],
  },
  {
    path: 'anlage-strategie',
    canActivate: [PotentialCustomerGuard],
    loadChildren: () => import('@zero/onboarding/feature-investment-strategy/web').then((m) => m.INVESTMENT_STRATEGY_ROUTES),
  },
];
